import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import { Badge } from "@/shared/components/ui/badge";
import { TruckIcon, CreditCard, User, FileText } from "lucide-react";
import { InvoicePaymentMethod } from "@/shared/types/enums";
import {
  INSTALLMENT_OPTIONS,
  MIN_INSTALLMENTS,
} from "@/features/rentals/constants";
import { formatCurrency, formatDate } from "@/shared/lib/formatters";
import type { RentalResponseDTO } from "@/shared/types/rental.types";
import type { PickupDTO } from "../types";

const PAYMENT_LABELS: Record<InvoicePaymentMethod, string> = {
  [InvoicePaymentMethod.CASH]: "Dinheiro",
  [InvoicePaymentMethod.CREDIT_CARD]: "Cartão de crédito",
  [InvoicePaymentMethod.DEBIT_CARD]: "Cartão de débito",
  [InvoicePaymentMethod.PIX]: "PIX",
  [InvoicePaymentMethod.BANK_TRANSFER]: "Transferência bancária",
};

interface PickupModalProps {
  open: boolean;
  rental: RentalResponseDTO | null;
  loading: boolean;
  onConfirm: (data: PickupDTO) => void;
  onClose: () => void;
}

export const PickupModal = ({
  open,
  rental,
  loading,
  onConfirm,
  onClose,
}: PickupModalProps) => {
  const [hasPayment, setHasPayment] = useState(false);
  const [paidValue, setPaidValue] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<InvoicePaymentMethod | "">(
    "",
  );
  const [installments, setInstallments] = useState<number>(MIN_INSTALLMENTS);
  const [invoiceObservation, setInvoiceObservation] = useState("");

  const handleClose = () => {
    setHasPayment(false);
    setPaidValue("");
    setPaymentMethod("");
    setInstallments(MIN_INSTALLMENTS);
    setInvoiceObservation("");
    onClose();
  };

  const handleConfirm = () => {
    const trimmedObservation = invoiceObservation.trim();
    const dto: PickupDTO = {
      payments:
        hasPayment && paidValue && paymentMethod
          ? [
              {
                paidValue: parseFloat(paidValue),
                paymentMethod,
                ...(paymentMethod === InvoicePaymentMethod.CREDIT_CARD
                  ? { installments }
                  : {}),
              },
            ]
          : [],
      ...(trimmedObservation ? { invoiceObservation: trimmedObservation } : {}),
    };
    onConfirm(dto);
  };

  const remaining = rental ? rental.totalValue - rental.paidValue : 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent
        className="max-w-lg"
        style={{
          backgroundColor: "#09090b",
          border: "1px solid #27272a",
          borderRadius: "0.75rem",
        }}
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div
              className="flex items-center justify-center w-10 h-10 rounded-full"
              style={{
                backgroundColor: "#1d4ed820",
                border: "1px solid #3b82f6",
              }}
            >
              <TruckIcon className="w-5 h-5" style={{ color: "#3b82f6" }} />
            </div>
            <div>
              <DialogTitle style={{ color: "#f4f4f5", fontSize: "1.15rem" }}>
                Liberar aluguel
              </DialogTitle>
              <DialogDescription style={{ color: "#a1a1aa" }}>
                Registrar saída das peças
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {rental && (
          <div className="space-y-4">
            <div
              className="rounded-lg p-4 space-y-3"
              style={{
                backgroundColor: "#1d4ed810",
                border: "1px solid #3b82f640",
              }}
            >
              <div
                className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide"
                style={{ color: "#3b82f6" }}
              >
                <User className="w-3.5 h-3.5" /> Detalhes do aluguel
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                <div>
                  <p className="text-xs" style={{ color: "#71717a" }}>
                    Contrato
                  </p>
                  <p
                    className="text-sm font-semibold"
                    style={{ color: "#f4f4f5" }}
                  >
                    {rental.contractNumber}
                  </p>
                </div>
                <div>
                  <p className="text-xs" style={{ color: "#71717a" }}>
                    Cliente
                  </p>
                  <p
                    className="text-sm font-semibold"
                    style={{ color: "#f4f4f5" }}
                  >
                    {rental.clientName}
                  </p>
                </div>
                <div>
                  <p className="text-xs" style={{ color: "#71717a" }}>
                    Saída prevista
                  </p>
                  <p
                    className="text-sm font-medium"
                    style={{ color: "#e5e7eb" }}
                  >
                    {formatDate(rental.expectedOutAt)}
                  </p>
                </div>
                <div>
                  <p className="text-xs" style={{ color: "#71717a" }}>
                    Retorno previsto
                  </p>
                  <p
                    className="text-sm font-medium"
                    style={{ color: "#e5e7eb" }}
                  >
                    {formatDate(rental.expectedInAt)}
                  </p>
                </div>
              </div>
            </div>

            <div
              className="rounded-lg p-4 space-y-2"
              style={{
                backgroundColor: "#18181b",
                border: "1px solid #27272a",
              }}
            >
              <div
                className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide"
                style={{ color: "#a1a1aa" }}
              >
                <FileText className="w-3.5 h-3.5" /> Situação financeira
              </div>
              <div className="grid grid-cols-3 gap-3 pt-1">
                <div
                  className="text-center rounded-md py-2"
                  style={{ backgroundColor: "#09090b" }}
                >
                  <p className="text-xs" style={{ color: "#71717a" }}>
                    Total
                  </p>
                  <p
                    className="text-base font-bold"
                    style={{ color: "#3b82f6" }}
                  >
                    {formatCurrency(rental.totalValue)}
                  </p>
                </div>
                <div
                  className="text-center rounded-md py-2"
                  style={{ backgroundColor: "#09090b" }}
                >
                  <p className="text-xs" style={{ color: "#71717a" }}>
                    Pago
                  </p>
                  <p
                    className="text-base font-bold"
                    style={{ color: "#22c55e" }}
                  >
                    {formatCurrency(rental.paidValue)}
                  </p>
                </div>
                <div
                  className="text-center rounded-md py-2"
                  style={{ backgroundColor: "#09090b" }}
                >
                  <p className="text-xs" style={{ color: "#71717a" }}>
                    Restante
                  </p>
                  <p
                    className="text-base font-bold"
                    style={{ color: remaining > 0 ? "#f97316" : "#22c55e" }}
                  >
                    {formatCurrency(remaining)}
                  </p>
                </div>
              </div>
            </div>

            <div
              className="rounded-lg p-4 space-y-3"
              style={{
                backgroundColor: "#18181b",
                border: "1px solid #27272a",
              }}
            >
              <label className="flex items-center gap-2.5 cursor-pointer select-none">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={hasPayment}
                    onChange={(e) => setHasPayment(e.target.checked)}
                    className="sr-only"
                  />
                  <div
                    className="w-4 h-4 rounded border flex items-center justify-center transition-colors"
                    style={{
                      backgroundColor: hasPayment ? "#3b82f6" : "transparent",
                      borderColor: hasPayment ? "#3b82f6" : "#52525b",
                    }}
                  >
                    {hasPayment && (
                      <svg
                        className="w-2.5 h-2.5"
                        viewBox="0 0 10 10"
                        fill="none"
                      >
                        <path
                          d="M1.5 5L4 7.5L8.5 2.5"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <CreditCard
                    className="w-4 h-4"
                    style={{ color: "#a1a1aa" }}
                  />
                  <span
                    className="text-sm font-medium"
                    style={{ color: "#e5e7eb" }}
                  >
                    Registrar pagamento na retirada
                  </span>
                </div>
              </label>

              {hasPayment && (
                <div
                  className="grid grid-cols-2 gap-3 pt-1"
                  style={{
                    borderTop: "1px solid #27272a",
                    paddingTop: "0.75rem",
                  }}
                >
                  <div className="space-y-1.5">
                    <label
                      className="text-xs font-medium"
                      style={{ color: "#a1a1aa" }}
                    >
                      Valor (R$)
                    </label>
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      placeholder="0,00"
                      value={paidValue}
                      onChange={(e) => setPaidValue(e.target.value)}
                      style={{
                        backgroundColor: "#09090b",
                        borderColor: "#3f3f46",
                        color: "#f4f4f5",
                      }}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label
                      className="text-xs font-medium"
                      style={{ color: "#a1a1aa" }}
                    >
                      Forma de pagamento
                    </label>
                    <select
                      value={paymentMethod}
                      onChange={(e) =>
                        setPaymentMethod(e.target.value as InvoicePaymentMethod)
                      }
                      className="h-10 w-full rounded-md border px-3 text-sm"
                      style={{
                        backgroundColor: "#09090b",
                        borderColor: "#3f3f46",
                        color: "#f4f4f5",
                      }}
                    >
                      <option value="">Selecione</option>
                      {Object.values(InvoicePaymentMethod).map((m) => (
                        <option key={m} value={m}>
                          {PAYMENT_LABELS[m]}
                        </option>
                      ))}
                    </select>
                  </div>
                  {paymentMethod === InvoicePaymentMethod.CREDIT_CARD ? (
                    <div className="space-y-1.5">
                      <label
                        className="text-xs font-medium"
                        style={{ color: "#a1a1aa" }}
                      >
                        Parcelamento
                      </label>
                      <select
                        value={installments}
                        onChange={(e) =>
                          setInstallments(Number(e.target.value))
                        }
                        className="h-10 w-full rounded-md border px-3 text-sm"
                        style={{
                          backgroundColor: "#09090b",
                          borderColor: "#3f3f46",
                          color: "#f4f4f5",
                        }}
                      >
                        {INSTALLMENT_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option === MIN_INSTALLMENTS
                              ? "À vista"
                              : `${option}x`}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            <div
              className="rounded-lg p-4 space-y-2"
              style={{
                backgroundColor: "#18181b",
                border: "1px solid #27272a",
              }}
            >
              <label
                className="text-xs font-medium uppercase tracking-wide block"
                style={{ color: "#a1a1aa" }}
              >
                Observação da NF
              </label>
              <Textarea
                placeholder="Descrição que aparece na nota fiscal"
                rows={3}
                maxLength={2000}
                value={invoiceObservation}
                onChange={(e) => setInvoiceObservation(e.target.value)}
                style={{
                  backgroundColor: "#09090b",
                  borderColor: "#3f3f46",
                  color: "#f4f4f5",
                }}
              />
              <p className="text-xs" style={{ color: "#71717a" }}>
                Opcional. Em branco usa o padrão da loja.
              </p>
            </div>

            <div
              className="rounded-lg px-4 py-3 flex items-center gap-3"
              style={{
                backgroundColor: "#1d4ed810",
                border: "1px solid #3b82f640",
              }}
            >
              <TruckIcon
                className="w-4 h-4 shrink-0"
                style={{ color: "#3b82f6" }}
              />
              <p className="text-sm" style={{ color: "#93c5fd" }}>
                Ao confirmar, as peças serão marcadas como{" "}
                <strong>em circulação</strong> e o status passará para{" "}
                <Badge
                  style={{
                    backgroundColor: "#1d4ed820",
                    color: "#3b82f6",
                    border: "1px solid #3b82f640",
                    fontSize: "0.7rem",
                  }}
                >
                  Ativo
                </Badge>
                .
              </p>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 pt-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={loading}
            style={{ borderColor: "#3f3f46", color: "#a1a1aa" }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={loading || (hasPayment && (!paidValue || !paymentMethod))}
            style={{ backgroundColor: "#3b82f6", color: "#fff" }}
            className="gap-2"
          >
            <TruckIcon className="w-4 h-4" />
            {loading ? "Processando..." : "Confirmar liberação"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
